import { useEffect, useCallback, useRef, useState } from "react";
import { useSocket } from "./useSocket";

/**
 * 📱 CHANNEL-SPECIFIC SOCKET HOOK
 * Handles real-time channel management (QR codes, connection status)
 * Based on backend Universal Socket Event Emitter
 */
export const useChannelSocket = ({
  channelId = null,
  onQRCode = null,
  onWhatsAppConnected = null,
  onWhatsAppDisconnected = null,
  onChannelCreated = null,
  onNewConversation = null,
} = {}) => {
  const { socket, isConnected } = useSocket();
  const [qrCode, setQrCode] = useState(null);
  const [qrExpiry, setQrExpiry] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [connectedInfo, setConnectedInfo] = useState(null);

  const handlersRef = useRef({
    onQRCode,
    onWhatsAppConnected,
    onWhatsAppDisconnected,
    onChannelCreated,
    onNewConversation,
  });

  // Update handlers ref when props change
  useEffect(() => {
    handlersRef.current = {
      onQRCode,
      onWhatsAppConnected,
      onWhatsAppDisconnected,
      onChannelCreated,
      onNewConversation,
    };
  }, [
    onQRCode,
    onWhatsAppConnected,
    onWhatsAppDisconnected,
    onChannelCreated,
    onNewConversation,
  ]);

  /**
   * 📨 Handle incoming channel events
   */
  const handleAppUpdate = useCallback(
    (event) => {
      const { type, data, channelId: eventChannelId } = event;

      // Filter events by channel if specified
      if (channelId && eventChannelId && eventChannelId !== channelId) {
        return;
      }

      // Handle different event types
      switch (type) {
        case "qr-code":
          setQrCode(data.qr);
          setQrExpiry(data.expires ? new Date(data.expires) : null);
          setConnectionStatus("connecting");

          if (handlersRef.current.onQRCode) {
            handlersRef.current.onQRCode({
              qr: data.qr,
              expires: data.expires,
              channelId: eventChannelId,
            });
          }
          break;

        case "whatsapp-connected":
          setConnectionStatus("connected");
          setQrCode(null); // Clear QR code when connected
          setQrExpiry(null);
          setConnectedInfo({
            phoneNumber: data.phoneNumber,
            accountName: data.accountName,
            status: data.status,
          });

          if (handlersRef.current.onWhatsAppConnected) {
            handlersRef.current.onWhatsAppConnected({
              phoneNumber: data.phoneNumber,
              accountName: data.accountName,
              status: data.status,
              channelId: eventChannelId,
            });
          }
          break;

        case "whatsapp-disconnected":
          setConnectionStatus("disconnected");
          setQrCode(null);
          setQrExpiry(null);
          setConnectedInfo(null);

          if (handlersRef.current.onWhatsAppDisconnected) {
            handlersRef.current.onWhatsAppDisconnected({
              reason: data.reason,
              status: data.status,
              channelId: eventChannelId,
            });
          }
          break;

        case "channel-created":
          // Extract QR code from authSetup if available
          if (data.authSetup?.qrCode) {
            setQrCode(data.authSetup.qrCode);
            if (data.authSetup.qrExpiry) {
              setQrExpiry(new Date(data.authSetup.qrExpiry));
            }
            console.log("🎯 QR Code extracted from channel-created event");
          }

          if (handlersRef.current.onChannelCreated) {
            handlersRef.current.onChannelCreated({
              channel: data.channel,
              authSetup: data.authSetup,
              channelId: eventChannelId,
            });
          }
          break;

        case "new-conversation":
          if (handlersRef.current.onNewConversation) {
            handlersRef.current.onNewConversation({
              conversation: data.conversation,
              customerId: data.customerId,
              customerName: data.customerName,
              channelName: data.channelName,
              isNew: data.isNew,
              channelId: eventChannelId,
            });
          }
          break;

        default:
          // Ignore other event types for channel
          break;
      }
    },
    [channelId]
  );

  /**
   * 🔄 Subscribe to socket events
   */
  useEffect(() => {
    if (!socket || !isConnected) return;

    socket.on("app-update", handleAppUpdate);

    return () => {
      socket.off("app-update", handleAppUpdate);
    };
  }, [socket, isConnected, handleAppUpdate]);

  /**
   * ⏰ QR Code expiry timer
   */
  useEffect(() => {
    if (!qrExpiry) return;

    const checkExpiry = () => {
      if (new Date() > qrExpiry) {
        setQrCode(null);
        setQrExpiry(null);
        if (connectionStatus === "connecting") {
          setConnectionStatus("disconnected");
        }
      }
    };

    const interval = setInterval(checkExpiry, 1000);
    return () => clearInterval(interval);
  }, [qrExpiry, connectionStatus]);

  /**
   * 📊 Get QR code time remaining
   */
  const getQRTimeRemaining = useCallback(() => {
    if (!qrExpiry) return null;

    const now = new Date();
    const remaining = qrExpiry.getTime() - now.getTime();

    if (remaining <= 0) return 0;

    return Math.floor(remaining / 1000); // Return seconds
  }, [qrExpiry]);

  /**
   * 🔄 Check if QR code is expired
   */
  const isQRExpired = useCallback(() => {
    if (!qrExpiry) return false;
    return new Date() > qrExpiry;
  }, [qrExpiry]);

  /**
   * 📊 Debug information
   */
  const getDebugInfo = useCallback(() => {
    return {
      isConnected,
      channelId,
      socketId: socket?.id,
      connectionStatus,
      hasQRCode: !!qrCode,
      qrExpiry: qrExpiry?.toISOString(),
      timeRemaining: getQRTimeRemaining(),
      connectedInfo,
      hasHandlers: {
        onQRCode: !!handlersRef.current.onQRCode,
        onWhatsAppConnected: !!handlersRef.current.onWhatsAppConnected,
        onWhatsAppDisconnected: !!handlersRef.current.onWhatsAppDisconnected,
        onChannelCreated: !!handlersRef.current.onChannelCreated,
        onNewConversation: !!handlersRef.current.onNewConversation,
      },
    };
  }, [
    isConnected,
    channelId,
    socket,
    connectionStatus,
    qrCode,
    qrExpiry,
    getQRTimeRemaining,
    connectedInfo,
  ]);

  return {
    // Connection status
    isConnected,
    connectionStatus,
    connectedInfo,

    // QR Code data
    qrCode,
    qrExpiry,
    getQRTimeRemaining,
    isQRExpired,

    // Status helpers
    isConnecting: connectionStatus === "connecting",
    isAuthenticated: connectionStatus === "connected",
    isReady: connectionStatus === "connected",
    hasError: connectionStatus === "error",

    // Debug
    getDebugInfo,
  };
};
